import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  BookmarkBorder,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FolderOpen,
  Star,
  StarBorder,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReportType, ReportField } from '../../pages/Reports/Reports';
import {
  SavedReportConfigListItem,
  getSavedReportConfigs,
  getSavedReportConfig,
  createSavedReportConfig,
  updateSavedReportConfig,
  deleteSavedReportConfig,
} from '../../services/reportsApi';
import { useAlert } from '../../contexts/AlertContext';
import styles from './SavedReportConfigs.module.css';

interface SavedReportConfigsProps {
  reportType: ReportType | null;
  fields: ReportField[];
  filters?: Record<string, any>;
  onLoadConfig: (reportType: ReportType, selectedFieldIds: string[], filters: Record<string, any>) => void;
}

const SavedReportConfigs: React.FC<SavedReportConfigsProps> = ({
  reportType,
  fields,
  filters = {},
  onLoadConfig,
}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [configs, setConfigs] = useState<SavedReportConfigListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeConfigId, setActiveConfigId] = useState<number | null>(null);

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null);
  const [configName, setConfigName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState<SavedReportConfigListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSavedReportConfigs(reportType || undefined);
      setConfigs(data);
    } catch (err: any) {
      console.error('[SavedReportConfigs] Failed to fetch configs:', err);
    } finally {
      setLoading(false);
    }
  }, [reportType]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Load a saved configuration
  const handleLoadConfig = async (config: SavedReportConfigListItem) => {
    try {
      const fullConfig = await getSavedReportConfig(config.id);
      setActiveConfigId(config.id);
      onLoadConfig(fullConfig.report_type, fullConfig.selected_fields, fullConfig.filters);
      showAlert('success', t('reports.savedConfigs.messages.loaded', { name: config.name }));
    } catch (err: any) {
      console.error('[SavedReportConfigs] Failed to load config:', err);
      showAlert('error', t('reports.savedConfigs.messages.loadError'));
    }
  };

  // Open save dialog (new)
  const handleOpenSaveDialog = () => {
    setEditingConfigId(null);
    setConfigName('');
    setIsDefault(false);
    setSaveDialogOpen(true);
  };

  // Open save dialog (edit/overwrite)
  const handleOpenEditDialog = async (e: React.MouseEvent, config: SavedReportConfigListItem) => {
    e.stopPropagation();
    setEditingConfigId(config.id);
    setConfigName(config.name);
    setIsDefault(config.is_default);
    setSaveDialogOpen(true);
  };

  // Save config
  const handleSave = async () => {
    if (!reportType) return;

    const selectedFieldIds = fields.filter(f => f.selected).map(f => f.id);
    if (selectedFieldIds.length === 0) {
      showAlert('error', t('reports.savedConfigs.messages.noFieldsSelected'));
      return;
    }

    if (!configName.trim()) {
      showAlert('error', t('reports.savedConfigs.messages.nameRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editingConfigId) {
        // Update existing
        await updateSavedReportConfig(editingConfigId, {
          name: configName.trim(),
          report_type: reportType,
          selected_fields: selectedFieldIds,
          filters: Object.keys(filters).length > 0 ? filters : undefined,
          is_default: isDefault,
        });
        showAlert('success', t('reports.savedConfigs.messages.updated', { name: configName.trim() }));
      } else {
        // Create new
        await createSavedReportConfig({
          name: configName.trim(),
          report_type: reportType,
          selected_fields: selectedFieldIds,
          filters: Object.keys(filters).length > 0 ? filters : undefined,
          is_default: isDefault,
        });
        showAlert('success', t('reports.savedConfigs.messages.saved', { name: configName.trim() }));
      }
      setSaveDialogOpen(false);
      fetchConfigs();
    } catch (err: any) {
      const errorMsg = err.response?.data?.name?.[0]
        || err.response?.data?.detail
        || t('reports.savedConfigs.messages.saveError');
      showAlert('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation
  const handleOpenDeleteDialog = (e: React.MouseEvent, config: SavedReportConfigListItem) => {
    e.stopPropagation();
    setDeletingConfig(config);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDelete = async () => {
    if (!deletingConfig) return;

    setDeleting(true);
    try {
      await deleteSavedReportConfig(deletingConfig.id);
      showAlert('success', t('reports.savedConfigs.messages.deleted', { name: deletingConfig.name }));
      if (activeConfigId === deletingConfig.id) {
        setActiveConfigId(null);
      }
      setDeleteDialogOpen(false);
      setDeletingConfig(null);
      fetchConfigs();
    } catch (err: any) {
      showAlert('error', t('reports.savedConfigs.messages.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  // Toggle default
  const handleToggleDefault = async (e: React.MouseEvent, config: SavedReportConfigListItem) => {
    e.stopPropagation();
    try {
      await updateSavedReportConfig(config.id, {
        is_default: !config.is_default,
      });
      fetchConfigs();
    } catch (err: any) {
      showAlert('error', t('reports.savedConfigs.messages.updateError'));
    }
  };

  const hasSelectedFields = fields.some(f => f.selected);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          <BookmarkBorder />
          {t('reports.savedConfigs.title')}
          {configs.length > 0 && (
            <span className={styles.configCount}>({configs.length})</span>
          )}
        </Typography>
        {reportType && hasSelectedFields && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleOpenSaveDialog}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              '&:hover': {
                borderColor: 'rgb(46, 125, 50)',
                backgroundColor: 'rgba(46, 125, 50, 0.1)',
              },
            }}
          >
            {t('reports.savedConfigs.saveButton')}
          </Button>
        )}
      </Box>

      {/* Config list */}
      {loading ? (
        <Box className={styles.loadingContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : configs.length === 0 ? (
        <Box className={styles.emptyState}>
          <FolderOpen className={styles.emptyIcon} />
          <Typography className={styles.emptyText}>
            {t('reports.savedConfigs.empty')}
          </Typography>
          <Typography className={styles.emptyHint}>
            {t('reports.savedConfigs.emptyHint')}
          </Typography>
        </Box>
      ) : (
        <Box className={styles.configList}>
          {configs.map((config) => (
            <Box
              key={config.id}
              className={`${styles.configItem} ${activeConfigId === config.id ? styles.active : ''}`}
              onClick={() => handleLoadConfig(config)}
            >
              <Box className={styles.configInfo}>
                <Typography className={styles.configName}>
                  {config.name}
                </Typography>
                <Box className={styles.configMeta}>
                  <Chip
                    label={config.report_type_display}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.6)',
                    }}
                  />
                  <span>
                    {t('reports.savedConfigs.fieldsCount', { count: config.fields_count })}
                  </span>
                  <span>{formatDate(config.updated_at)}</span>
                </Box>
              </Box>
              <Box className={styles.configActions}>
                <Tooltip title={config.is_default
                  ? t('reports.savedConfigs.removeDefault')
                  : t('reports.savedConfigs.setDefault')
                }>
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggleDefault(e, config)}
                    sx={{ color: config.is_default ? '#ffc107' : 'rgba(255, 255, 255, 0.4)' }}
                  >
                    {config.is_default ? <Star /> : <StarBorder />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('reports.savedConfigs.overwrite')}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenEditDialog(e, config)}
                    sx={{ color: 'rgba(255, 255, 255, 0.4)', '&:hover': { color: '#ffffff' } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('reports.savedConfigs.deleteButton')}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenDeleteDialog(e, config)}
                    sx={{ color: 'rgba(255, 255, 255, 0.4)', '&:hover': { color: '#f44336' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Save/Edit Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
            border: '1px solid #474747',
          },
        }}
      >
        <DialogTitle>
          {editingConfigId
            ? t('reports.savedConfigs.editDialogTitle')
            : t('reports.savedConfigs.saveDialogTitle')
          }
        </DialogTitle>
        <DialogContent>
          <Box className={styles.dialogContent}>
            <TextField
              autoFocus
              fullWidth
              label={t('reports.savedConfigs.nameLabel')}
              placeholder={t('reports.savedConfigs.namePlaceholder')}
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && configName.trim()) {
                  handleSave();
                }
              }}
              InputProps={{
                sx: { color: '#ffffff' },
              }}
              InputLabelProps={{
                sx: { color: 'rgba(255, 255, 255, 0.7)' },
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            {reportType && (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                {t('reports.savedConfigs.saveInfo', {
                  type: t(`reports.types.${reportType}`),
                  count: fields.filter(f => f.selected).length,
                })}
              </Typography>
            )}
            <FormControlLabel
              className={styles.defaultCheckbox}
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&.Mui-checked': { color: '#ffc107' },
                  }}
                />
              }
              label={t('reports.savedConfigs.setAsDefault')}
              sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setSaveDialogOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {t('reports.savedConfigs.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !configName.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              backgroundColor: 'rgb(46, 125, 50)',
              '&:hover': { backgroundColor: 'rgb(35, 94, 38)' },
            }}
          >
            {saving
              ? t('reports.savedConfigs.saving')
              : t('reports.savedConfigs.saveConfirm')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
            border: '1px solid #474747',
          },
        }}
      >
        <DialogTitle>
          {t('reports.savedConfigs.deleteDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            {t('reports.savedConfigs.deleteConfirmation', { name: deletingConfig?.name })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {t('reports.savedConfigs.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{
              backgroundColor: '#f44336',
              '&:hover': { backgroundColor: '#d32f2f' },
            }}
          >
            {deleting
              ? t('reports.savedConfigs.deleting')
              : t('reports.savedConfigs.deleteConfirm')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SavedReportConfigs;
